import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { ErrorAlert, SettingsProfileResult } from '@xdocs/schemas';
import { useToast } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';
import { FTUE_STEPS } from './ftueSteps.js';
import { useFtueT } from './useFtueT.js';

export interface UseFtueResult {
  /** Whether the tour overlay should be rendered right now. */
  isOpen: boolean;
  /** Current step (0-indexed); always a valid index into `FTUE_STEPS`. */
  stepIndex: number;
  stepCount: number;
  isLastStep: boolean;
  isFirstStep: boolean;
  next: () => void;
  back: () => void;
  /** Skip at any step — persists completion immediately (F9: "one click + obvious", "don't nag"). */
  skip: () => void;
  /** Reaching the final step's CTA — same persistence as skip, different origin for analytics/copy. */
  finish: () => void;
  /** Explicit re-entry point for `/settings` "Replay tutorial" (F9 "re-runnable from Settings"). */
  replay: () => void;
  isPersisting: boolean;
}

/**
 * FTUE (F9) tour state. Auto-launches once on first login (`users
 * .ftueCompletedAt IS NULL`, read via the already-wired `settings.getProfile`
 * op — no new read op needed, see `apps/server/src/ftue/WIRING.md`), and
 * exposes `replay()` for the settings-page re-entry point. Persists
 * completion (whether reached naturally or skipped — "for users who
 * skipped (don't nag)") via `ftue.complete`, which is idempotent server-side.
 *
 * "Resuming an interrupted FTUE" (F9 edge case): step position is NOT
 * persisted server-side (out of scope — a single boolean flag is the only
 * entity this feature owns per FEATURES.md). A refresh mid-tour restarts
 * from step 0 rather than losing the tour entirely — for a 4-6 step, ~1
 * minute tutorial this is the documented, deliberate simplification (see
 * this task's returned open-issues list) over adding new server state for a
 * skippable, replayable, low-stakes onboarding flourish.
 *
 * E20 (§5.10-1): a transient WS reconnect (heartbeat miss, tab backgrounding)
 * flips `connectionState` through `reconnecting`/`offline` without tearing
 * down client state. `connectionState`/`enabled` therefore only gate the
 * ONE-TIME decision to auto-launch the tour (`autoOpened`, below); once the
 * tour has opened it stays open (and keeps `stepIndex`) through any later
 * connection dip — only `skip()`/`finish()` (or a full page refresh, the
 * documented exception above) close it.
 */
export function useFtue(): UseFtueResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useFtueT();
  const enabled = connectionState === 'connected' && userId !== null;

  const [stepIndex, setStepIndex] = useState(0);
  const [replaying, setReplaying] = useState(false);

  const profileQuery = useQuery<SettingsProfileResult, ErrorAlert>({
    queryKey: ['user', userId, 'settings', 'profile'],
    queryFn: () => requestTyped(wsClient, 'settings.getProfile', undefined),
    enabled,
    staleTime: Infinity,
  });

  const completeMutation = useMutation<{ ftueCompletedAt: Date }, ErrorAlert, void>({
    mutationFn: () => requestTyped(wsClient, 'ftue.complete', undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['user', userId, 'settings', 'profile'] });
    },
    onError: (err) => {
      // Never swallow: the tour has already closed (`dismissedThisSession`
      // is set before this fires), so the failure is otherwise completely
      // invisible. Log for diagnostics and surface a low-stakes toast — the
      // user isn't blocked on this, they'll just be offered the tour again
      // next session if it never persisted.
      console.error('useFtue: ftue.complete failed', err);
      toast.push({ tone: 'error', message: t('ftue:error.completeFailed') });
    },
  });

  // A same-tab-session "already offered" guard so a refetch of `settings
  // .getProfile` (e.g. after some unrelated invalidation) never re-opens the
  // tour mid-session once the user has dismissed/finished it once — the
  // server flag is the source of truth across sessions, this is purely a
  // same-session debounce.
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  // One-shot latch: once the auto-launch condition is met, the tour is
  // considered opened for the rest of the session regardless of later
  // `connectionState` dips (E20 §5.10-1). This is what decouples `isOpen`
  // from live reconnect/offline flicker.
  const [autoOpened, setAutoOpened] = useState(false);

  const neverCompleted = profileQuery.data?.ftueCompletedAt == null;

  useEffect(() => {
    if (!autoOpened && enabled && profileQuery.isSuccess && neverCompleted && !dismissedThisSession) {
      setAutoOpened(true);
    }
  }, [autoOpened, enabled, profileQuery.isSuccess, neverCompleted, dismissedThisSession]);

  const isOpen = replaying || (autoOpened && !dismissedThisSession);

  useEffect(() => {
    if (!isOpen) setStepIndex(0);
  }, [isOpen]);

  const persistCompletion = useCallback(() => {
    setDismissedThisSession(true);
    setReplaying(false);
    setAutoOpened(false);
    // `.mutate()` (not `mutateAsync`) — nothing here awaits the result, and
    // `mutateAsync`'s returned promise still rejects even after `onError`
    // runs, which would otherwise be an unhandled rejection.
    if (neverCompleted) completeMutation.mutate();
  }, [completeMutation, neverCompleted]);

  const next = useCallback(() => {
    setStepIndex((i) => Math.min(i + 1, FTUE_STEPS.length - 1));
  }, []);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(i - 1, 0));
  }, []);

  const skip = useCallback(() => {
    persistCompletion();
  }, [persistCompletion]);

  const finish = useCallback(() => {
    persistCompletion();
  }, [persistCompletion]);

  const replay = useCallback(() => {
    setStepIndex(0);
    setReplaying(true);
  }, []);

  return {
    isOpen,
    stepIndex,
    stepCount: FTUE_STEPS.length,
    isLastStep: stepIndex === FTUE_STEPS.length - 1,
    isFirstStep: stepIndex === 0,
    next,
    back,
    skip,
    finish,
    replay,
    isPersisting: completeMutation.isPending,
  };
}
